import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import InfoIcon from "@mui/icons-material/Info";
import dayjs, { Dayjs } from "dayjs";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  normalizePayloadList,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import { golfTooltipHeaders, spaTooltipHeaders } from "../../../constants/TooltipInformation";
import { t } from "../../../tooltips/tooltipRuntimeCache";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import {
  GolfPropertyEnum,
  SpaPropertyEnum,
} from "../../../constants/NavbarList";
import { PercentageorFlatAmount } from "../../../constants/GuaranteeField";
import { removeSampleTestArray } from "../../actions/cleanup";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { useSearchParams } from "react-router-dom";
import {
  getProductType,
  ProductEnum,
  SearchParams,
} from "../../../constants/login";

const defaultTaxDate = () => dayjs();

const resolveTaxesFromFieldValues = (fieldValues: any): any[] => {
  if (!fieldValues) {
    return [];
  }
  return (
    normalizePayloadList(
      fieldValues[GolfPropertyEnum.taxes] ?? fieldValues[SpaPropertyEnum.taxes],
    ) ?? []
  );
};

const resolveOutletSetupFromFieldValues = (fieldValues: any) =>
  fieldValues?.[GolfPropertyEnum.outletSetup] ??
  fieldValues?.[SpaPropertyEnum.outletSetup];

const parsePercentageOrFlatFromRow = (value: unknown): string | null => {
  if (value === true) {
    return "Percentage";
  }
  if (value === false) {
    return "Flat Amount";
  }
  if (value === "%" || value === "Percentage") {
    return "Percentage";
  }
  if (value === "$" || value === "Flat Amount") {
    return "Flat Amount";
  }
  if (
    typeof value === "string" &&
    PercentageorFlatAmount.includes(
      value as (typeof PercentageorFlatAmount)[number],
    )
  ) {
    return value;
  }
  return null;
};

const parseTaxFlag = (value: unknown): boolean =>
  value === true || value === "Yes" || value === "True" || value === "true";

const Taxes = ({ data, fieldValues }: { data: any; fieldValues: any }) => {
  const [searchParams] = useSearchParams();
  const isSpaLogin =
    getProductType(Number(searchParams.get(SearchParams.PRODUCT))) ===
    ProductEnum.SPA;
  const outletTooltip = isSpaLogin
    ? spaTooltipHeaders.taxOutlet
    : golfTooltipHeaders.defaultOutlet;
  const isGolfProduct =
    fieldValues != null &&
    Object.prototype.hasOwnProperty.call(fieldValues, GolfPropertyEnum.taxes);
  const [outlet, setOutlet] = useState<string | null>(null);
  const [taxName, setTaxName] = useState<string | null>(null);
  const [percentageOrFlatAmount, setPercentageOrFlatAmount] = useState<
    string | null
  >(null);
  const [taxValue, setTaxValue] = useState<string | null>(null);
  const [allowTaxOnServiceCharge, setAllowTaxOnServiceCharge] =
    useState<boolean>(false);
  const [allowTaxOnGratuity, setAllowTaxOnGratuity] = useState<boolean>(false);
  const [defaultToAllRetailItems, setDefaultToAllRetailItems] =
    useState<boolean>(false);
  const [hasTaxOnTax, setHasTaxOnTax] = useState<boolean>(false);
  const [allowRemovalForPaymentMethod, setAllowRemovalForPaymentMethod] =
    useState<boolean>(false);
  const [excludeForTaxExemption, setExcludeForTaxExemption] =
    useState<boolean>(false);
  const [taxStartDate, setTaxStartDate] = useState<Dayjs | null>(
    defaultTaxDate,
  );
  const [taxEndDate, setTaxEndDate] = useState<Dayjs | null>(defaultTaxDate);
  const [outletErr, setOutletErr] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);
  const [percentageErr, setPercentageErr] = useState<boolean>(false);
  const [valueErr, setValueErr] = useState<boolean>(false);
  const [startDateErr, setStartDateErr] = useState<boolean>(false);
  const [endDateErr, setEndDateErr] = useState<boolean>(false);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [GolfTaxes, setGolfTaxes] = useState<any>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [outletList, setOutletList] = useState<string[]>([]);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const payload = {
    Outlet: outlet,
    "Tax Name": taxName ? String(taxName).trim() : taxName,
    "Allow Tax on Service Charge": allowTaxOnServiceCharge,
    "Allow Tax on Gratuity": allowTaxOnGratuity,
    "Percentage or Flat Amount": percentageOrFlatAmount === "Percentage",
    Value: taxValue === null || taxValue === "" ? null : Number(taxValue),
    "Start Date": taxStartDate ? taxStartDate.format("YYYY-MM-DD") : "",
    "End Date": taxEndDate ? taxEndDate.format("YYYY-MM-DD") : "",
    "Default to all Retail Items": defaultToAllRetailItems,
    ...(isGolfProduct
      ? {
          "Charge Tax on Tax": hasTaxOnTax,
          "Allow Removal for Payment Method": allowRemovalForPaymentMethod,
          "Exclude for Tax Exemption": excludeForTaxExemption,
        }
      : {}),
  };

  const validateInput = (): boolean => {
    setOutletErr(false);
    setNameError(false);
    setPercentageErr(false);
    setValueErr(false);
    setStartDateErr(false);
    setEndDateErr(false);
    let ok = true;

    if (!outlet || String(outlet).trim() === "") {
      setOutletErr(true);
      ok = false;
    } else if (
      !validateString(
        String(outlet).trim(),
        validationLength.outletName,
        validationType.SPACE,
      )
    ) {
      setOutletErr(true);
      ok = false;
    }

    const trimmedTaxName = String(taxName ?? "").trim();
    if (!trimmedTaxName) {
      setNameError(true);
      ok = false;
    } else if (
      !validateString(
        trimmedTaxName,
        validationLength.taxType,
        validationType.CODE,
      )
    ) {
      setNameError(true);
      ok = false;
    }

    if (!percentageOrFlatAmount || percentageOrFlatAmount === "") {
      setPercentageErr(true);
      ok = false;
    }

    if (!taxValue || taxValue === "") {
      setValueErr(true);
      ok = false;
    } else if (!/^\d+(\.\d+)?$/.test(taxValue)) {
      setValueErr(true);
      ok = false;
    }

    if (!taxStartDate) {
      setStartDateErr(true);
      ok = false;
    }

    if (!taxEndDate) {
      setEndDateErr(true);
      ok = false;
    } else if (taxStartDate && taxEndDate.isBefore(taxStartDate, "day")) {
      setEndDateErr(true);
      ok = false;
    }

    if (!ok) {
      setAlertMessage("Please correct the highlighted fields");
      setAlertType("error");
      setOpenAlert(true);
    }

    return ok;
  };

  const editGolfTaxes = (list: any) => {
    if (list == null) {
      setSelectedId(null);
      setEditClicked(false);
      setOutlet(null);
      setTaxName(null);
      setPercentageOrFlatAmount(null);
      setTaxValue(null);
      setTaxStartDate(defaultTaxDate());
      setTaxEndDate(defaultTaxDate());
      setAllowTaxOnServiceCharge(false);
      setAllowTaxOnGratuity(false);
      setDefaultToAllRetailItems(false);
      setHasTaxOnTax(false);
      setAllowRemovalForPaymentMethod(false);
      setExcludeForTaxExemption(false);
      setOutletErr(false);
      setNameError(false);
      setPercentageErr(false);
      setValueErr(false);
      setStartDateErr(false);
      setEndDateErr(false);
      return;
    }

    list?.id != null && setSelectedId(list.id);
    setEditClicked(true);
    setOutlet(
      list?.Outlet != null && list.Outlet !== "" ? String(list.Outlet) : null,
    );
    setTaxName(list?.["Tax Name"] ?? null);
    setAllowTaxOnServiceCharge(
      list?.["Allow Tax on Service Charge"] === true ||
        list?.["Allow Tax on Service Charge"] === "Yes",
    );
    setAllowTaxOnGratuity(
      list?.["Allow Tax on Gratuity"] === true ||
        list?.["Allow Tax on Gratuity"] === "Yes",
    );
    setPercentageOrFlatAmount(
      parsePercentageOrFlatFromRow(list?.["Percentage or Flat Amount"]),
    );
    setTaxValue(
      list?.Value != null && list.Value !== "" ? String(list.Value) : null,
    );
    const loadedStart = list?.["Start Date"]
      ? dayjs(list["Start Date"])
      : defaultTaxDate();
    setTaxStartDate(loadedStart);
    const loadedEndRaw = list?.["End Date"]
      ? dayjs(list["End Date"])
      : loadedStart;
    setTaxEndDate(
      loadedEndRaw.isBefore(loadedStart, "day") ? loadedStart : loadedEndRaw,
    );
    setDefaultToAllRetailItems(
      list?.["Default to all Retail Items"] === true ||
        list?.["Default to all Retail Items"] === "Yes",
    );
    setHasTaxOnTax(parseTaxFlag(list?.["Charge Tax on Tax"]));
    setAllowRemovalForPaymentMethod(
      parseTaxFlag(list?.["Allow Removal for Payment Method"]),
    );
    setExcludeForTaxExemption(
      parseTaxFlag(list?.["Exclude for Tax Exemption"]),
    );
    setOutletErr(false);
    setNameError(false);
    setPercentageErr(false);
    setValueErr(false);
    setStartDateErr(false);
    setEndDateErr(false);
  };

  const updateGolfTaxes = () => {
    if (!validateInput()) {
      return;
    }
    const taxesList = normalizePayloadList(GolfTaxes) ?? [];
    if (
      checkDuplicate(
        taxesList.filter((item: any) => String(item.id) !== String(selectedId)),
        DNDFieldsEnum.TAXNAME2,
        String(taxName ?? "").trim(),
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setGolfTaxes((prev: any) => {
      const list = normalizePayloadList(prev) ?? [];
      return list.map((item: any) =>
        String(item.id) === String(selectedId) ? { ...item, ...payload } : item,
      );
    });
    editGolfTaxes(null);
    setAlertMessage("Taxes updated successfully");
    setAlertType("success");
    setOpenAlert(true);
  };

  const addGolfTaxes = () => {
    if (!validateInput()) {
      return;
    }
    const taxesList = normalizePayloadList(GolfTaxes) ?? [];
    const trimmedTaxName = String(taxName ?? "").trim();
    if (checkDuplicate(taxesList, DNDFieldsEnum.TAXNAME2, trimmedTaxName)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setGolfTaxes((prev: any) => {
      const list = normalizePayloadList(prev) ?? [];
      return updateData(
        list.length
          ? [...list, { id: list.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      );
    });
    setLastUpdatedRecord(payload);
    editGolfTaxes(null);
  };

  const deleteGolfTaxes = (id: number) => {
    setGolfTaxes(deleteItem(normalizePayloadList(GolfTaxes) ?? [], id));
  };

  useEffect(() => {
    if (fieldValues) {
      setGolfTaxes(resolveTaxesFromFieldValues(fieldValues));
      setOutletList(
        fillAutocompleteValues(
          resolveOutletSetupFromFieldValues(fieldValues),
          DNDFieldsEnum.OUTLETNAME,
          "",
        ),
      );
    }
  }, []);

  useEffect(() => {
    data(GolfTaxes);
    editClicked &&
      setSelectedId(checkData(GolfTaxes, selectedId) ? selectedId : null);
  }, [GolfTaxes]);

  useEffect(() => {
    const trimmedTaxName = String(taxName ?? "").trim();
    if (!trimmedTaxName) {
      setNameError(false);
      return;
    }
    setNameError(
      !validateString(
        trimmedTaxName,
        validationLength.taxType,
        validationType.CODE,
      ),
    );
  }, [taxName]);

  const endDateHelperText = endDateErr
    ? !taxEndDate
      ? validationMessages.fieldRequired
      : validationMessages.endDateBeforeStart
    : "";

  return (
    <Stack direction="column" gap="10px">
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Taxes
              <Tooltip
                title={tooltipComponent(
                  t(
                    "golfTooltipHeaders",
                    "taxesHeaders",
                    golfTooltipHeaders.taxesHeaders,
                  ),
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editGolfTaxes(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton click={() => editGolfTaxes(lastUpdatedRecord)} />
              )}
            </Stack>
          </Stack>
          <Stack direction="column" width="100%" gap="20px">
            <Stack
              direction="row"
              width="100%"
              gap="5px"
              alignItems="flex-start"
            >
              <Autocomplete
                id="outletList"
                disablePortal
                options={removeSampleTestArray(outletList)}
                sx={{ width: "100%" }}
                value={outlet}
                disabled={!outletList?.length}
                onChange={(_e, newValue) => {
                  setOutlet(newValue ?? null);
                  setOutletErr(false);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Outlet *"
                    variant="outlined"
                    error={outletErr}
                    helperText={
                      outletErr ? validationMessages.fieldRequired : ""
                    }
                  />
                )}
              />
              <Tooltip title={tooltipComponent(outletTooltip)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Tax Name *
                <Tooltip
                  title={tooltipComponent(
                    t(
                      "golfTooltipHeaders",
                      "taxName",
                      golfTooltipHeaders.taxName,
                    ),
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="TaxName"
                variant="outlined"
                placeholder="Tax Name"
                value={taxName ?? ""}
                helperText={
                  nameError
                    ? !taxName || String(taxName).trim() === ""
                      ? validationMessages.fieldRequired
                      : validationMessages.taxType
                    : ""
                }
                error={nameError}
                onChange={(e) => {
                  setTaxName(e.target.value.toUpperCase() || null);
                }}
                sx={{ width: "100%" }}
              />
            </Stack>
          </Stack>

          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            width="100%"
          >
            <Stack direction="row" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    id="AllowTaxOnServiceCharge"
                    checked={allowTaxOnServiceCharge}
                    onChange={(e) =>
                      setAllowTaxOnServiceCharge(e.target.checked)
                    }
                  />
                }
                label="Allow Tax on Service Charge"
              />
              <Tooltip title={tooltipComponent(golfTooltipHeaders.allowTaxonServiceCharge)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <Stack direction="row" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    id="AllowTaxOnGratuity"
                    checked={allowTaxOnGratuity}
                    onChange={(e) => setAllowTaxOnGratuity(e.target.checked)}
                  />
                }
                label="Allow Tax on Gratuity"
              />
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.allowTaxonGratuity)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>

          <FormLabel component="legend">
            Percentage or Flat Amount *
            <Tooltip
              title={tooltipComponent(
                golfTooltipHeaders.percentageOrFlatAmount,
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <FormControl fullWidth error={percentageErr}>
            <Select
              id="percentageOrFlatAmount"
              displayEmpty
              value={percentageOrFlatAmount ?? ""}
              onChange={(e) => {
                setPercentageOrFlatAmount(
                  e.target.value === "" ? null : String(e.target.value),
                );
                setPercentageErr(false);
              }}
              sx={{ width: "100%" }}
            >
              <MenuItem value="">
                <em>Select</em>
              </MenuItem>
              {PercentageorFlatAmount.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {percentageErr ? (
              <FormHelperText>
                {validationMessages.fieldRequired}
              </FormHelperText>
            ) : null}
          </FormControl>

          <Stack direction="column" width="100%" gap="5px">
            <FormLabel component="legend">
              Value *
              <Tooltip title={tooltipComponent(golfTooltipHeaders.value)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <TextField
              id="Value"
              variant="outlined"
              placeholder="Value"
              value={taxValue ?? ""}
              error={valueErr}
              helperText={valueErr ? validationMessages.fieldRequired : ""}
              sx={{ width: "100%" }}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "" || /^\d*\.?\d*$/.test(raw)) {
                  setTaxValue(raw || null);
                  setValueErr(false);
                }
              }}
            />
          </Stack>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Stack direction="column" width="100%" gap="16px">
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  Start Date *
                  <Tooltip
                    title={tooltipComponent(golfTooltipHeaders.startDateTax)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <DatePicker
                  value={taxStartDate}
                  onChange={(v) => {
                    setTaxStartDate(v);
                    setStartDateErr(false);
                    if (v && taxEndDate && taxEndDate.isBefore(v, "day")) {
                      setTaxEndDate(v);
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: "Start Date",
                      error: startDateErr,
                      helperText: startDateErr
                        ? validationMessages.fieldRequired
                        : "",
                    },
                  }}
                />
              </Stack>
              <Stack direction="column" width="100%" gap="5px">
                <FormLabel component="legend">
                  End Date *
                  <Tooltip
                    title={tooltipComponent(golfTooltipHeaders.endDateTax)}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </FormLabel>
                <DatePicker
                  value={taxEndDate}
                  minDate={taxStartDate ?? undefined}
                  onChange={(v) => {
                    setEndDateErr(false);
                    if (!v) {
                      setTaxEndDate(null);
                      return;
                    }
                    if (taxStartDate && v.isBefore(taxStartDate, "day")) {
                      setAlertMessage(validationMessages.endDateBeforeStart);
                      setAlertType("error");
                      setOpenAlert(true);
                      setTaxEndDate(taxStartDate);
                      return;
                    }
                    setTaxEndDate(v);
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      placeholder: "End Date",
                      error: endDateErr,
                      helperText: endDateHelperText,
                    },
                  }}
                />
              </Stack>
            </Stack>
          </LocalizationProvider>

          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            width="100%"
          >
            <Stack direction="row" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    id="defaultToAllRetailItems"
                    checked={defaultToAllRetailItems}
                    onChange={(e) =>
                      setDefaultToAllRetailItems(e.target.checked)
                    }
                  />
                }
                label="Default to All Retail Items"
              />
              <Tooltip
                title={tooltipComponent(golfTooltipHeaders.defaultToAllItems)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
          </Stack>

          {isGolfProduct && (
            <Stack
              className="form-checkbox-row form-checkbox-row--three"
              direction="row"
              width="100%"
            >
              <Stack direction="row" alignItems="center" gap="4px">
                <FormControlLabel
                  control={
                    <Checkbox
                      id="hasTaxOnTax"
                      checked={hasTaxOnTax}
                      onChange={(e) => setHasTaxOnTax(e.target.checked)}
                    />
                  }
                  label="Charge Tax on Tax"
                />
                <Tooltip
                  title={tooltipComponent(golfTooltipHeaders.hasTaxOnTax)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Stack direction="row" alignItems="center" gap="4px">
                <FormControlLabel
                  control={
                    <Checkbox
                      id="allowRemovalForPaymentMethod"
                      checked={allowRemovalForPaymentMethod}
                      onChange={(e) =>
                        setAllowRemovalForPaymentMethod(e.target.checked)
                      }
                    />
                  }
                  label="Allow removal for Payment Method"
                />
                <Tooltip
                  title={tooltipComponent(
                    golfTooltipHeaders.allowRemovalForPaymentMethod,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
              <Stack direction="row" alignItems="center" gap="4px">
                <FormControlLabel
                  control={
                    <Checkbox
                      id="excludeForTaxExemption"
                      checked={excludeForTaxExemption}
                      onChange={(e) =>
                        setExcludeForTaxExemption(e.target.checked)
                      }
                    />
                  }
                  label="Exclude for Tax exemption"
                />
                <Tooltip
                  title={tooltipComponent(
                    golfTooltipHeaders.excludeForTaxExemption,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </Stack>
            </Stack>
          )}

          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="contained"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={() => (selectedId ? updateGolfTaxes() : addGolfTaxes())}
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Tax
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Typography variant="h6">Taxes Available</Typography>
            <Stack direction="column" gap="10px">
              {GolfTaxes?.length ? (
                <DraggableItemList
                  itemData={GolfTaxes}
                  onOrderUpdate={(order) => setGolfTaxes(order)}
                  onEdit={(info) => editGolfTaxes(info)}
                  onDelete={(id) => deleteGolfTaxes(id)}
                  code={DNDFieldsEnum.OUTLET}
                  name={DNDFieldsEnum.TAXNAME2}
                />
              ) : (
                <Typography variant="body2">
                  No Taxes Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default Taxes;
